import * as PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocales } from '../../locales';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import PlaceholderImage from '../../assets/images/image-place-holder.jpg';

import IsUpdatingIndicator from '../isUpdatingIndicator';
import {
  FACEBOOK,
  FACEBOOK_GROUP,
  GOOGLE,
  INSTAGRAM,
  LINKEDIN_BUSINESS_PAGE,
  LINKEDIN_PERSONAL_ACCOUNT,
  TWITTER,
  YOUTUBE,
  PLATFORM_LIST
} from '../../config/constants';
import { AnalyticsTable } from './table';
import { ChartContainer, SmallChart } from './chartsContainer';
import { PLATFORMS } from '../../config/metrics';
import {
  ButtonGroup,
  Collapse,
  OverlayTrigger,
  ToggleButton,
  Tooltip
} from 'react-bootstrap';
import {
  IS_CREATED_VIA_MULTIPOST_ICON,
  IS_DOWNLOADABLE_ICON,
  IS_SHAREABLE_ICON,
  IS_STORY_ICON,
} from "../../config/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { duotone } from "@fortawesome/fontawesome-svg-core/import.macro";
import { LomavisBadgeList1 } from '@/lomavisSharedUi/molecules/badges/LomavisBadgeList1';
import { LomavisDateTimeFormatter1 } from '@/lomavisSharedUi/atoms/utils/datetime/LomavisDateTimeFormatter1';

export function PostShortAnalytics({ post, inView }) {
  const order = PLATFORM_LIST;
  const VALUE_GENERAL = '1';
  const VALUE_IG_STORY = '2';

  const { demo } = useSelector((state) => state.locationDetails);
  const { currentLang, translate: t } = useLocales();

  const [instagramStoryAnalyticsOpen, setInstagramStoryAnalyticsOpen] =
    useState(false);

  const [availablePlatforms] = useState(
    post.analytics?.data_as_json?.published
      .map((platform) => (platform.published ? platform.platform : null))
      .filter((a) => a !== null)
      .sort((a, b) => {
        const indexA = order.indexOf(a);
        const indexB = order.indexOf(b);

        return indexA - indexB;
      }) || []
  );

  const {
    data: { [post.uuid]: postDetails }
  } = useSelector((state) => state.shortAnalytics);

  const [showOverviewAnalytics, setShowOverviewAnalytics] = useState(true);
  useEffect(() => {
    if (post) {
      // Google does not support post analytics
      const onlyPostedOnGoogle =
        post?.analytics?.data_as_json?.published?.length === 1 &&
        post?.analytics?.data_as_json?.published[0].platform === GOOGLE;
      if (onlyPostedOnGoogle) {
        setShowOverviewAnalytics(false);
      }
    }
  });

  /*
   *
   * */
  const toggleAnalytics = () => {
    setInstagramStoryAnalyticsOpen(!instagramStoryAnalyticsOpen);
  };

  return (
    <div className="card card-custom gutter-b">
      <div className="card-body pb-1 pt-4 px-0 px-md-5">
        <div className="d-flex flex-start position-absolute">
          {postDetails?.isUpdating ? <IsUpdatingIndicator /> : ''}
        </div>
        <div className="d-flex flex-wrap w-100 mb-2">
          <div className="col-12 col-md-6 col-lg-6">
            <div className="d-flex gap-2 flex-warp align-items-center">
              {post?.is_shared_post && (
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip>{t('post_types.post_is_shareable')}</Tooltip>
                  }
                >
                  <i className={`${IS_SHAREABLE_ICON} me-2 ms-2`}></i>
                </OverlayTrigger>
              )}
              {!post?.created_in_lomavis && (
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>{t('post_is_imported')}</Tooltip>}
                >
                  <i className={`${IS_DOWNLOADABLE_ICON} me-2`}></i>
                </OverlayTrigger>
              )}
              {post.created_as_multipost && (
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip>{t('post_is_created_via_multipost')}</Tooltip>
                  }
                >
                  <i className={`${IS_CREATED_VIA_MULTIPOST_ICON} me-2`}></i>
                </OverlayTrigger>
              )}
              {post.content_type_is_story_post && (
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>{t('post_is_story')}</Tooltip>}
                >
                  <i className={`${IS_STORY_ICON} me-2`}></i>
                </OverlayTrigger>
              )}

              {post?.campaigns?.length > 0 ? (
                <LomavisBadgeList1
                  items={post.campaigns.map((t) => ({
                    id: t.uuid,
                    title: t.name,
                    color: t.color
                  }))}
                  maxVisible={3}
                />
              ) : null}
            </div>
          </div>
          <div className="col-12 col-md-6 col-lg-6 d-flex justify-content-end align-items-center">
            <div>
              <small>
                <LomavisDateTimeFormatter1 dateTimeStr={post.publication_date} />
              </small>
            </div>

            <div className="text-muted mx-2">
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>{t('short_analytics_info')}</Tooltip>}
              >
                <i className="fa-duotone fa-circle-info fa-sm"></i>
              </OverlayTrigger>
            </div>
          </div>
        </div>

        <div className="d-flex flex-row align-items-center justify-content-end">
          {post?.analytics?.data_as_json?.additional_data
            ?.instagram_story_analytics ? (
            <div
              className="d-flex flex-row align-items-center justify-content-end cursor-pointer"
              onClick={toggleAnalytics}
            >
              <div>
                <span>
                  <i className="fa-brands fa-instagram me-1 text-danger"></i>
                </span>
                <span className="fw-bolder text-muted">
                  {t('instagram_story_analytics')}
                </span>
              </div>
              <div>
                <button className="btn btn-sm btn-white">
                  {instagramStoryAnalyticsOpen ? (
                    <i className="fa-duotone fa-minus"></i>
                  ) : (
                    <i className="fa-duotone fa-plus"></i>
                  )}
                </button>
              </div>
            </div>
          ) : (
            ''
          )}
        </div>

        <div className="d-flex flex-column flex-lg-row">
          <div className="col-12 col-lg-5 d-flex align-items-stretch">
            <div className="col-4 d-flex align-items-center">
              <div className="w-100 d-flex justify-content-center w-100">
                <div className="position-absolute justify-content-center align-self-center w-100  d-flex">
                  <div className="d-flex align-items-center">
                    <div>
                      {post.is_video === true ? (
                        <i className="far fa-play-circle fs-4x text-light-primary"></i>
                      ) : (
                        ''
                      )}
                    </div>
                  </div>
                </div>
                {post.preview_image_url ? (
                  <LazyLoadImage
                    className="mw-100 img-thumbnail  mh-150px"
                    src={post.preview_image_url}
                  />
                ) : (
                  <img
                    src={PlaceholderImage}
                    className="mw-100 img-thumbnail  mh-150px"
                  />
                )}
              </div>
            </div>

            <div className="col-8 d-flex align-items-center">
              {post.preview_html ? (
                <div
                  className="w-100 overflow-hidden align-items-center text-secondary mh-100px px-4 text-dark"
                  style={{ whiteSpace: 'pre-wrap' }}
                  dangerouslySetInnerHTML={{ __html: post.preview_html }}
                ></div>
              ) : (
                post.is_shared_post && (
                  <div className=" w-100 overflow-hidden align-items-center text-secondary mh-100px px-4 text-muted text-center">
                    {t('topPosts.post_is_shared_without_comment')}
                  </div>
                )
              )}
            </div>
          </div>

          <div className="col-12 col-lg-7 flex-grow-1 d-flex justify-content-center align-items-center">
            {showOverviewAnalytics ? (
              <AnalyticsTable
                platforms={demo ? PLATFORMS : availablePlatforms}
                isVideo={post.is_video && false}
                post={post?.analytics?.data_as_json}
              />
            ) : (
              <div className={'text-muted mx-auto w-75 text-center'}>
                {t('no_data_for_google_only_post')}
              </div>
            )}
          </div>
        </div>

        {post?.analytics?.data_as_json?.additional_data
          ?.instagram_story_analytics ? (
          <div className={'d-flex  justify-content-end col-12 my-5'}>
            <div>
              <Collapse in={instagramStoryAnalyticsOpen}>
                <div>
                  <div
                    className={
                      'd-flex flex-column justify-content-end w-100 container'
                    }
                  >
                    <div className={'d-flexm align-items-center'}>
                      <span>
                        <i className="fa-brands fa-instagram me-1 fs-1 text-danger"></i>
                      </span>
                      <span className={'fw-bold fs-2'}>
                        {t('instagram_story_analytics_title')}
                      </span>
                    </div>
                    <div
                      className={
                        'd-flex flex-row flex-wrap justify-content-end'
                      }
                    >
                      <div className={'col-4 col-sm-auto mt-5 mx-5'}>
                        <div>
                          <span className={'fw-bolder'}>
                            {t('instagram_story_analytics.follower')}
                          </span>
                        </div>

                        <div>
                          <table
                            className={'table table-borderless  table-striped'}
                          >
                            <tbody>
                              <tr>
                                <td className={'px-2'}>
                                  {t('instagram_story_analytics.follows')}
                                </td>
                                <td className={' px-2 text-center'}>
                                  {
                                    post?.analytics?.data_as_json
                                      ?.additional_data
                                      ?.instagram_story_analytics?.data?.follows
                                  }
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div></div>
                        <div></div>
                        <div></div>
                      </div>

                      <div className={'col-4 col-sm-auto mt-5 mx-5'}>
                        <div>
                          <span className={'fw-bolder'}>
                            {t('instagram_story_analytics.performance')}
                          </span>
                        </div>

                        <div>
                          <table
                            className={'table table-borderless  table-striped'}
                          >
                            <tbody>
                              <tr>
                                <td className={'px-2'}>
                                  {t('instagram_story_analytics.impressions')}
                                </td>
                                <td className={' px-2 text-center'}>
                                  {
                                    post?.analytics?.data_as_json
                                      ?.additional_data
                                      ?.instagram_story_analytics?.data
                                      ?.impressions
                                  }
                                </td>
                              </tr>
                              <tr>
                                <td className={' px-2'}>
                                  {t('instagram_story_analytics.reach')}
                                </td>
                                <td className={' px-2 text-center'}>
                                  {
                                    post?.analytics?.data_as_json
                                      ?.additional_data
                                      ?.instagram_story_analytics?.data?.reach
                                  }
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div></div>
                        <div></div>
                        <div></div>
                      </div>

                      <div className={'col-4 col-sm-auto mt-5 mx-5'}>
                        <div>
                          <span className={'fw-bolder'}>
                            {t('instagram_story_analytics.profile')}
                          </span>
                        </div>

                        <div>
                          <table
                            className={'table table-borderless  table-striped'}
                          >
                            <tbody>
                              <tr>
                                <td className={' px-2'}>
                                  {t(
                                    'instagram_story_analytics.profile_activity'
                                  )}
                                </td>
                                <td className={' px-2 text-center'}>
                                  {
                                    post?.analytics?.data_as_json
                                      ?.additional_data
                                      ?.instagram_story_analytics?.data
                                      ?.profile_activity
                                  }
                                </td>
                              </tr>

                              <tr>
                                <td className={' px-2'}>
                                  {t(
                                    'instagram_story_analytics.profile_visits'
                                  )}
                                </td>
                                <td className={' px-2 text-center'}>
                                  {
                                    post?.analytics?.data_as_json
                                      ?.additional_data
                                      ?.instagram_story_analytics?.data
                                      ?.profile_visits
                                  }
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div></div>
                        <div></div>
                        <div></div>
                      </div>

                      <div className={'col-4 col-sm-auto mt-5 mx-5'}>
                        <div>
                          <span className={'fw-bolder'}>
                            {t('instagram_story_analytics.interactions')}
                          </span>
                        </div>

                        <div>
                          <table
                            className={'table table-borderless  table-striped'}
                          >
                            <tbody>
                              <tr>
                                <td className={' px-2'}>
                                  {t('instagram_story_analytics.replies')}
                                </td>
                                <td className={' px-2 text-center'}>
                                  {
                                    post?.analytics?.data_as_json
                                      ?.additional_data
                                      ?.instagram_story_analytics?.data?.replies
                                  }
                                </td>
                              </tr>
                              <tr>
                                <td className={' px-2'}>
                                  {t('instagram_story_analytics.shares')}
                                </td>
                                <td className={' px-2 text-center'}>
                                  {
                                    post?.analytics?.data_as_json
                                      ?.additional_data
                                      ?.instagram_story_analytics?.data?.shares
                                  }
                                </td>
                              </tr>

                              <tr>
                                <td className={' px-2'}>
                                  {t(
                                    'instagram_story_analytics.total_interactions'
                                  )}
                                </td>
                                <td className={' px-2 text-center'}>
                                  {
                                    post?.analytics?.data_as_json
                                      ?.additional_data
                                      ?.instagram_story_analytics?.data
                                      ?.total_interactions
                                  }
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div></div>
                        <div></div>
                        <div></div>
                      </div>

                      <div className={'col-4 col-sm-auto mt-5 mx-5'}>
                        <div>
                          <span className={'fw-bolder'}>
                            {t('instagram_story_analytics.navigation')}
                          </span>
                        </div>

                        <div>
                          <table
                            className={'table table-borderless table-striped'}
                          >
                            <tbody>
                              <tr>
                                <td className={' px-2'}>
                                  {t('instagram_story_analytics.swipe_forward')}
                                </td>
                                <td className={' px-2 text-center'}>
                                  {
                                    post?.analytics?.data_as_json
                                      ?.additional_data
                                      ?.instagram_story_analytics
                                      ?.navigation_breakdown?.swipe_forward
                                  }
                                </td>
                              </tr>
                              <tr>
                                <td className={' px-2'}>
                                  {t('instagram_story_analytics.tap_forward')}
                                </td>
                                <td className={' px-2 text-center'}>
                                  {
                                    post?.analytics?.data_as_json
                                      ?.additional_data
                                      ?.instagram_story_analytics
                                      ?.navigation_breakdown?.tap_forward
                                  }
                                </td>
                              </tr>

                              <tr>
                                <td className={' px-2'}>
                                  {t('instagram_story_analytics.tap_back')}
                                </td>
                                <td className={' px-2 text-center'}>
                                  {
                                    post?.analytics?.data_as_json
                                      ?.additional_data
                                      ?.instagram_story_analytics
                                      ?.navigation_breakdown?.tap_back
                                  }
                                </td>
                              </tr>

                              <tr>
                                <td className={' px-2'}>
                                  {t('instagram_story_analytics.tap_exit')}
                                </td>
                                <td className={' px-2 text-center'}>
                                  {
                                    post?.analytics?.data_as_json
                                      ?.additional_data
                                      ?.instagram_story_analytics
                                      ?.navigation_breakdown?.tap_exit
                                  }
                                </td>
                              </tr>

                              <tr className={''}>
                                <td className={' px-2'}>
                                  {t(
                                    'instagram_story_analytics.navigation_total'
                                  )}
                                </td>
                                <td className={' px-2 text-center'}>
                                  {
                                    post?.analytics?.data_as_json
                                      ?.additional_data
                                      ?.instagram_story_analytics?.data
                                      ?.navigation
                                  }
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div></div>
                        <div></div>
                        <div></div>
                      </div>
                    </div>
                  </div>
                </div>
              </Collapse>
            </div>
          </div>
        ) : (
          ''
        )}

        {post?.analytics?.data_as_json?.summary && showOverviewAnalytics && (
          <ChartContainer
            summary={{
              ...post?.analytics?.data_as_json?.summary,
              post_performance_rating: post?.analytics?.post_performance_rating
            }}
          />
        )}
      </div>
      <div className="card-footer">
        <div className="d-flex w-100 justify-content-end">
          {post?.analytics?.data_as_json?.summary && showOverviewAnalytics && (
            <a
              href={post.analytics_url}
              className="btn fw-bold btn-sm btn-primary btn-shadow mx-1"
            >
              {t('analytics')}
            </a>
          )}
          <a
            href={post.edit_url}
            className="btn fw-bold btn-sm btn-warning btn-shadow mx-1"
          >
            {t('view')}
          </a>
        </div>
      </div>
    </div>
  );
}

PostShortAnalytics.propTypes = {
  post: PropTypes.object,
  inView: PropTypes.bool
};
